import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Horizontal marquee-style ticker of the latest published impact stories.
 * Hides itself if no stories are available so it never shows a stale banner.
 */
const ImpactStoriesTicker = () => {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/stories?limit=8`);
        if (alive) setStories(Array.isArray(data) ? data : []);
      } catch (_e) {
        if (alive) setStories([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!stories.length) return null;

  // Duplicate the list so the marquee loop is seamless
  const loop = [...stories, ...stories];

  return (
    <section
      className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white border-y border-amber-700/30 overflow-hidden"
      data-testid="impact-stories-ticker"
      aria-label="Latest Impact Stories"
    >
      <div className="flex items-stretch">
        {/* Label */}
        <Link
          to="/impact-stories"
          className="flex items-center gap-2 bg-kioo-primary text-white font-bold px-4 md:px-6 py-3 flex-shrink-0 hover:bg-kioo-secondary transition-colors z-10 shadow-md"
          data-testid="impact-ticker-label"
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-2 h-2 bg-red-300 rounded-full animate-pulse"></span>
            <span className="whitespace-nowrap text-sm md:text-base uppercase tracking-wide">Impact Stories</span>
          </span>
        </Link>

        {/* Scrolling track */}
        <div className="relative flex-1 overflow-hidden py-3">
          <div className="flex items-center gap-10 whitespace-nowrap marquee-track">
            {loop.map((s, idx) => (
              <Link
                key={`${s.id}-${idx}`}
                to="/impact-stories"
                className="inline-flex items-center gap-2 hover:text-yellow-100 transition-colors"
                data-testid={idx < stories.length ? `impact-ticker-item-${s.id}` : undefined}
              >
                <span className="text-lg">📖</span>
                <span className="font-semibold text-sm md:text-base">{s.title}</span>
                {s.location && (
                  <span className="text-amber-100 text-xs md:text-sm">— {s.location}</span>
                )}
                <span className="mx-4 text-amber-200">•</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .marquee-track {
          animation: marquee-scroll 45s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default ImpactStoriesTicker;
